<template>
  <div class="px-6 py-6 space-y-6 min-h-screen select-none text-left transition-colors duration-200 font-sans"
    :class="isDark ? 'bg-zinc-950 text-zinc-400' : 'bg-zinc-50 text-zinc-600'">
    
    <!-- Entête de la fiche -->
    <div class="flex sm:flex-row flex-col justify-between items-start sm:items-center border-b pb-5 gap-4 transition-colors duration-200"
      :class="isDark ? 'border-zinc-900' : 'border-zinc-200'">
      <div class="space-y-0.5">
        <button @click="$emit('back')" class="text-[10px] font-semibold uppercase tracking-wider hover:underline transition-colors flex items-center gap-1"
          :class="isDark ? 'text-zinc-500 hover:text-white' : 'text-zinc-400 hover:text-zinc-800'">
          ← Retour au registre
        </button>
        <h1 class="text-xl font-semibold tracking-tight" :class="isDark ? 'text-white' : 'text-zinc-900'">{{ employee.nom }}</h1>
      </div>
      
      <div class="flex gap-3 sm:w-auto w-full">
        <button @click="toggleStatus" 
          class="flex-1 sm:flex-none px-4 py-2 rounded-xl text-xs font-semibold uppercase tracking-wider border transition-all"
          :class="employee.actif 
            ? (isDark ? 'bg-emerald-950/20 text-emerald-400 border-emerald-900/40' : 'bg-emerald-50 text-emerald-700 border-emerald-200') 
            : (isDark ? 'bg-zinc-900 text-zinc-500 border-zinc-800' : 'bg-zinc-100 text-zinc-400 border-zinc-200')">
          {{ employee.actif ? 'Opérateur actif' : 'Opérateur suspendu' }}
        </button>
        <button @click="$emit('edit', employee)" 
          class="flex-1 sm:flex-none px-5 py-2.5 rounded-xl text-xs font-semibold uppercase tracking-wider transition-all shadow-sm"
          :class="isDark ? 'bg-white hover:bg-zinc-200 text-black' : 'bg-zinc-900 hover:bg-zinc-800 text-white'">
          Ajuster la fiche
        </button>
      </div>
    </div>

    <!-- Contenu principal -->
    <div class="grid lg:grid-cols-3 gap-6 items-start w-full">
      
      <!-- Colonne gauche : Profil et Stats -->
      <div class="space-y-6 lg:col-span-1">
        
        <div class="border rounded-2xl p-5 space-y-4 transition-colors"
          :class="isDark ? 'bg-zinc-900/20 border-zinc-900' : 'bg-white border-zinc-200 shadow-sm'">
          <div class="flex items-center gap-4">
            <img :src="employee.avatar || 'https://ui-avatars.com/api/?background=27272a&color=fff&name=' + employee.nom" 
              alt="Avatar" class="w-16 h-16 rounded-full object-cover border" :class="isDark ? 'border-zinc-800' : 'border-zinc-200'" />
            <div class="space-y-0.5 text-left">
              <span class="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                :class="isDark ? 'bg-zinc-800 text-zinc-500' : 'bg-zinc-100 text-zinc-500'">{{ employee.role }}</span>
              <h2 class="text-sm font-semibold" :class="isDark ? 'text-white' : 'text-zinc-900'">{{ employee.nom }}</h2>
              <p class="text-[10px] font-semibold uppercase tracking-wider" :class="isDark ? 'text-amber-500/80' : 'text-amber-600'">{{ employee.specialite }}</p>
            </div>
          </div>

          <div class="pt-4 border-t space-y-2 text-xs font-medium" :class="isDark ? 'border-zinc-900/60' : 'border-zinc-100'">
            <div class="flex justify-between"><span :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Email</span><span :class="isDark ? 'text-zinc-300' : 'text-zinc-700'">{{ employee.email }}</span></div>
            <div class="flex justify-between"><span :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Contrat</span><span :class="isDark ? 'text-zinc-300' : 'text-zinc-700'">{{ employee.heuresContrat }}h / sem.</span></div>
          </div>
        </div>

        <!-- Stats -->
        <div class="border rounded-2xl p-5 space-y-4 transition-colors"
          :class="isDark ? 'bg-zinc-900/20 border-zinc-900' : 'bg-white border-zinc-200 shadow-sm'">
          <h3 class="text-[10px] uppercase tracking-widest font-bold" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Performance mensuelle</h3>
          <div class="grid grid-cols-2 gap-3">
            <div class="p-3.5 rounded-xl border transition-colors" :class="isDark ? 'bg-zinc-950 border-zinc-900' : 'bg-zinc-50 border-zinc-100'">
              <p class="text-[9px] uppercase font-semibold" :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">C.A. Généré</p>
              <p class="text-base font-bold" :class="isDark ? 'text-white' : 'text-zinc-900'">{{ employee.stats.caGenere }} €</p>
            </div>
            <div class="p-3.5 rounded-xl border transition-colors" :class="isDark ? 'bg-zinc-950 border-zinc-900' : 'bg-zinc-50 border-zinc-100'">
              <p class="text-[9px] uppercase font-semibold" :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">Taux Occup.</p>
              <p class="text-base font-bold text-emerald-500">{{ employee.stats.tauxOccupation }}%</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Colonne droite : Planning -->
      <div class="space-y-6 lg:col-span-2">
        <div class="border rounded-2xl p-5 space-y-4 transition-colors"
          :class="isDark ? 'bg-zinc-900/20 border-zinc-900' : 'bg-white border-zinc-200 shadow-sm'">
          <div class="flex justify-between items-center border-b pb-3" :class="isDark ? 'border-zinc-900' : 'border-zinc-100'">
            <h3 class="text-sm font-semibold" :class="isDark ? 'text-white' : 'text-zinc-900'">Planning & actes assignés</h3>
            <span class="text-[10px] font-semibold px-2 py-0.5 rounded" :class="isDark ? 'bg-zinc-900 text-zinc-500' : 'bg-zinc-100 text-zinc-500'">
              {{ employee.agendaAssignment?.length || 0 }} sessions
            </span>
          </div>

          <div class="overflow-x-auto">
            <table class="w-full text-xs text-left">
              <thead class="uppercase text-[9px] font-bold" :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">
                <tr><th class="pb-3 font-bold">Date</th><th class="pb-3 font-bold">Client</th><th class="pb-3 font-bold">Prestation</th><th class="pb-3 font-bold text-right">Tarif</th></tr>
              </thead>
              <tbody class="divide-y" :class="isDark ? 'divide-zinc-900/40' : 'divide-zinc-100'">
                <tr v-for="session in employee.agendaAssignment" :key="session.id" class="transition-colors" :class="isDark ? 'hover:bg-zinc-900/30' : 'hover:bg-zinc-50'">
                  <td class="py-3 pr-2 font-medium" :class="isDark ? 'text-zinc-300' : 'text-zinc-700'">{{ formatDate(session.date) }}</td>
                  <td class="py-3 pr-2 font-semibold" :class="isDark ? 'text-white' : 'text-zinc-900'">{{ session.client }}</td>
                  <td class="py-3 pr-2 text-zinc-500 truncate max-w-[150px]">{{ session.service }}</td>
                  <td class="py-3 text-right font-bold" :class="isDark ? 'text-white' : 'text-zinc-900'">{{ session.prix }} €</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { toast } from 'vue3-toastify'
import api from '@/api'
import { useTheme } from '@/composables/useTheme';

// Variable globale injectée ou reçue par props pour basculer de thème
const { isDark } = useTheme();
const route = useRoute()

// Props modeling high-fidelity data extraction for an operational operator profile
const props = defineProps({
  employee: {
    type: Object,
    default: () => null
  }
})

const employee = ref({
  id: null,
  nom: '',
  email: '',
  role: '',
  specialite: '',
  heuresContrat: 35,
  actif: true,
  avatar: '',
  stats: {
    caGenere: 0,
    tauxOccupation: 0,
    tauxRetention: 0,
    nbPrestations: 0
  },
  agendaAssignment: []
})

// Fetch staff details and appointments from backend
const fetchStaffDetails = async () => {
  const staffId = route.params.id || props.employee?.id

  if (!staffId) {
    toast.error('ID du collaborateur non spécifié')
    return
  }

  try {
    // Fetch staff info
    const [staffResponse, statsResponse, appointmentsResponse] = await Promise.all([
      api.staff.getStaffById(staffId),
      api.staff.getStaffStats(staffId).catch(() => ({ data: null })),
      api.appointments.getStaffAppointments(staffId).catch(() => ({ data: [] }))
    ])

    const staffData = staffResponse.data
    employee.value = {
      id: staffData._id,
      nom: staffData.nom,
      email: staffData.email,
      role: staffData.role || 'Collaborateur',
      specialite: staffData.specialite || '',
      heuresContrat: staffData.heuresContrat || 35,
      actif: staffData.statut === 'actif',
      avatar: staffData.avatar || '',
      stats: statsResponse.data || {
        caGenere: 0,
        tauxOccupation: 0,
        tauxRetention: 0,
        nbPrestations: 0
      },
      agendaAssignment: (appointmentsResponse.data || []).map(apt => {
        const start = new Date(apt.start)
        const end = new Date(apt.end)
        return {
          id: apt._id,
          date: apt.start,
          heure: `${start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} - ${end.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`,
          client: apt.nom,
          service: apt.service?.map(s => s.nom).join(', ') || '',
          prix: apt.montant || 0
        }
      })
    }
  } catch (error) {
    console.error('Erreur lors du chargement des détails du collaborateur', error)
    toast.error('Erreur lors du chargement des détails')
  }
}

// Local interactions dispatch adjustments
const toggleStatus = async () => {
  try {
    const newStatus = employee.value.actif ? 'inactif' : 'actif'
    await api.staff.updateStaff(employee.value.id, { statut: newStatus })
    employee.value.actif = !employee.value.actif
    toast.success(`Statut mis à jour`, { position: 'top-right' })
  } catch (error) {
    toast.error('Erreur lors de la mise à jour du statut')
  }
}

const formatDate = (dateStr) => {
  if (!dateStr) return 'Inconnue'
  return new Date(dateStr).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })
}

// Load data on mount
onMounted(() => {
  if (props.employee) {
    employee.value = { ...props.employee }
  } else {
    fetchStaffDetails()
  }
})
</script>

<style scoped>
/* Core system scrolling resets */
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>